/**
 * Framework-agnostic IntersectionObserver for lazy mounting.
 *
 * Components below the fold are observed and mounted only when
 * they come within 200px of the viewport. Eager mounting is
 * requested with `data-eager` on the container, which bypasses
 * the observer entirely so the component is rendered
 * immediately.
 *
 * The observer is a shared singleton per page: the same
 * instance handles every eligible container so one
 * IntersectionObserver receives all entries, keeping native
 * callback cost negligible compared to one observer per
 * container.
 *
 * The class is generic over the mount context type so each
 * framework can carry its own componentMap + devServer blob
 * through the observer without the core knowing its shape.
 */

const LAZY_ROOT_MARGIN = '200px 0px';

interface ContainerWithContext<Ctx> extends HTMLElement {
  _sdxMountContext?: Ctx;
}

export class LazyMountObserver<Ctx> {
  private observer: IntersectionObserver | null = null;

  constructor(private readonly mount: (container: HTMLElement, ctx: Ctx) => void) {}

  observe(container: HTMLElement, ctx: Ctx, fallbackCtx: Ctx): void {
    const io = this.ensureObserver(fallbackCtx);
    if (!io) {
      // No IntersectionObserver support: mount immediately.
      this.mount(container, ctx);
      return;
    }
    (container as ContainerWithContext<Ctx>)._sdxMountContext = ctx;
    io.observe(container);
  }

  unobserve(container: HTMLElement): void {
    this.observer?.unobserve(container);
  }

  private ensureObserver(fallbackCtx: Ctx): IntersectionObserver | null {
    if (this.observer) return this.observer;
    if (typeof IntersectionObserver === 'undefined') {
      return null;
    }
    this.observer = new IntersectionObserver(
      (entries, io) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          const target = entry.target as ContainerWithContext<Ctx>;
          const ctx = target._sdxMountContext ?? fallbackCtx;
          this.mount(target as HTMLElement, ctx);
        }
      },
      { rootMargin: LAZY_ROOT_MARGIN },
    );
    return this.observer;
  }
}

/**
 * Finds every SDX component container within the given
 * context. Includes the context element itself when it
 * matches the selector (Drupal behaviors pass the root
 * element, not a parent).
 */
export function findContainers(context: Element | Document): HTMLElement[] {
  const containers: HTMLElement[] = [];
  if (
    context.nodeType === Node.ELEMENT_NODE
    && (context as Element).matches?.('.sdx-component[data-component]')
  ) {
    containers.push(context as HTMLElement);
  }
  const inner = context.querySelectorAll<HTMLElement>('.sdx-component[data-component]');
  inner.forEach((el) => containers.push(el));
  return containers;
}
